/**
 * Tipos de domínio do PAN.
 *
 * No app do monorepo esses tipos eram implícitos: viviam nos JSON Schemas de
 * entidade publicados no ERPrompt-API (`@schemas/entities/pan.*.json`) e a UI
 * era montada por interpretação de layout em runtime. Aqui o contrato é
 * explícito e verificado em build — a aplicação é dona do próprio domínio.
 */

export type Saude = 'OK' | 'Atencao' | 'Incidente';
export type Papel = 'Controladora' | 'Controlada' | 'Instalação';
export type TipoInstalacao = 'Cloud' | 'On-premises';
export type StatusContrato = 'Ativo' | 'Inativo' | 'Suspenso';
/** "Falha"/"Falha Parcial" são os valores reais gravados pelo worker
 * (`run_finalizar_instalacao_fastu`, `pan_status`) — não "Falhou". O mesmo
 * campo (`status` do documento em `schedulings`) passa por todos esses
 * valores ao longo da vida de um agendamento: Programado → Executando →
 * Cancelado OU Concluído/Falha/Falha Parcial. */
export type StatusAgendamento = 'Programado' | 'Executando' | 'Cancelado' | 'Concluído' | 'Falha' | 'Falha Parcial';
/** Histórico nunca lista 'Executando' — só o que já chegou a um estado final
 * (ou foi cancelado antes de rodar). Ver `listar_historico_atualizacoes`. */
export type StatusHistorico = 'Concluído' | 'Falha' | 'Falha Parcial' | 'Programado' | 'Cancelado';

export interface ModuloContratado {
  id: string;
  nome: string;
  inicio: string | null;
  saude: string | null;
}

export interface Contrato {
  id: string | null;
  status: StatusContrato | null;
  dataContratacao: string | null;
  modulos: ModuloContratado[];
}

export interface Cliente {
  id: string | null;
  nome: string | null;
  contrato: Contrato | null;
}

export interface Instalacao {
  id: string;
  codigo: string | null;
  nome: string;
  clienteId: string;
  cliente: Cliente | null;
  tipo: TipoInstalacao | null;
  regiao: string | null;
  versaoAtual: string;
  versaoEstavelRecomendada: string;
  ultimaAtualizacao: string | null;
  saude: Saude | null;
  papel: Papel | null;
  /** Administrador do tenant (Diretório), com cascata de Controladora para Controladas. */
  administrador: boolean;
  isControladora: boolean;
  controladoraId: string | null;
  cloudCode: string | null;
  /** Há versão mais nova aplicável a esta instalação? Governa o fluxo de agendar. */
  atualizar: boolean;
  /** Algum tenant da instalação é cliente ANA (`diretorio.tenants.clienteana`). */
  ana: boolean;
  /** Cliente interno da Nasajon (`diretorio.clientes.interno`). */
  interno: boolean;
  /** Textos de escopo calculados pelo backend (cards da Home). */
  escopo: {
    total: string | null;
    texto: string | null;
    saude: string | null;
    saudeDescricao: string | null;
    modo: string | null;
  };
}

export interface Agendamento {
  id: string;
  instalacaoId: string;
  instalacao: string;
  versao: string;
  data: string | null;
  hora: string;
  status: StatusAgendamento;
  origem: string;
  batchId: string | null;
  createdAt: string | null;
}

export interface EventoHistorico {
  id: string;
  instalacao: string;
  versao: string;
  data: string | null;
  status: StatusHistorico;
  origem: string;
  batchId: string | null;
}

export interface Novidade {
  id: string;
  version: string;
  category: string;
  module: string;
  title: string;
  summary: string;
  type: string;
  PO: string;
  link: string;
  imageUrl: string;
}

/** Os únicos valores que `POST /novidades` aceita pro campo `type` — ver
 * `apps/v2/novidades/schemas.py` (`Literal`). Notas antigas importadas do
 * sistema anterior podem ter outros valores (ex.: "Correctiva"), mas isso é
 * só pra exibição retroativa — não algo que uma novidade nova deveria usar. */
export const TIPOS_NOVIDADE = ['Melhoria', 'Nova funcionalidade', 'Adaptativa legal'] as const;
export type TipoNovidade = (typeof TIPOS_NOVIDADE)[number];

/** As 12 categorias de produto listadas em nasajon.com.br/historico-atualizacoes
 * — fechada de propósito (não é o `module` granular tipo "FS1 · Scritta SQL"
 * que os dados legados usam, e que o site público não expõe). */
export const MODULOS_NOVIDADE = [
  'Contábil',
  'Finanças',
  'Scritta',
  'Persona',
  'Estoque',
  'Compras',
  'PDV',
  'Serviços',
  'Locações',
  'CRM',
  'Migrador',
  'Admin',
] as const;

export type NovidadeInput = Omit<Novidade, 'id'>;

/** Selo de uma versão — só informativo, não muda elegibilidade (isso já veio
 * filtrado pelo backend). `retentativa`: só aparece personificando — é a
 * própria versão atual do cliente, reaplicada. */
export type TagVersao = 'recomendada' | 'homologada' | 'nao_homologada' | 'retentativa';

export interface VersaoDisponivel {
  id: string;
  nome: string;
  semAtualizacoesDisponiveis: boolean;
  tag?: TagVersao;
}

/**
 * Uma guia do seletor de versão — LD, QA ou Master (ou "Fixo", quando a
 * instalação tem um ramo preferido travado pelo admin, sem guias). Guias
 * abaixo do estágio atual da instalação simplesmente não vêm do backend.
 */
export interface GuiaVersao {
  chave: string;
  rotulo: string;
  ramo: string;
  versoes: VersaoDisponivel[];
}

export interface VersoesInstalacao {
  versaoAtualCliente: string;
  /** Chave da guia em que a instalação está hoje (`null` quando o ramo dela
   * não é mais um dos ativos, ou quando há ramo preferido fixo). */
  estagioAtual: string | null;
  guias: GuiaVersao[];
}

export interface HorarioDisponivel {
  id: string;
  horario: string;
  data: string;
  disponivel: number;
  total: number;
  /** Rótulo pronto: "09:00 · 4 de 5 vagas". */
  nome: string;
}

/** Payload da criação de agendamento (um lote, N instalações alvo). */
export interface NovoAgendamento {
  versao: string;
  data: string;
  hora: string;
  instalacoesAlvo: string[];
  instalacaoAcesso: string;
  /** Retenção temporária feita ao selecionar o horário (ver `reterHorario`), se houver. */
  holdId?: string | null;
}

/** Retenção temporária (alguns minutos) de vaga(s) num horário, feita ao selecionar — antes de confirmar. */
export interface RetencaoHorario {
  holdId: string;
  ttlSegundos: number;
  /** ISO — quando a retenção expira sozinha se não for confirmada. */
  expiraEm: string;
  /**
   * "HH:mm" — quando a atualização vai disparar de verdade, não só a hora
   * cheia escolhida: instalações do mesmo horário são espaçadas por pelo
   * menos 2 minutos (o worker dispara cada uma na sua vez, não todas juntas).
   * Estimativa no momento da retenção — a posição de verdade só fecha ao
   * confirmar, uma retenção/confirmação concorrente pode mudar isso até lá.
   * Igual a `horarioRealFim` quando o lote tem 1 instalação.
   */
  horarioRealInicio: string;
  /** "HH:mm" — horário real da ÚLTIMA instalação do lote (ver `horarioRealInicio`). */
  horarioRealFim: string;
}

/** Contrato que qualquer fonte de dados (REST real ou demo) precisa cumprir. */
export interface PanDataSource {
  readonly kind: 'rest' | 'demo';
  listInstalacoes(signal?: AbortSignal): Promise<Instalacao[]>;
  /**
   * Uma página das instalações do escopo, com busca e filtros no servidor —
   * para telas que precisam navegar por centenas de instalações (Agendar).
   */
  listInstalacoesPaginado(filtro: FiltroInstalacoes, signal?: AbortSignal): Promise<PaginaInstalacoes>;
  listAgendamentos(signal?: AbortSignal): Promise<Agendamento[]>;
  /**
   * "Meus Agendamentos" — escopado por QUEM pediu, não pelas instalações do
   * escopo do usuário: ao contrário de `listAgendamentos` (hub de todas as
   * instalações acessíveis), aqui é só o que a própria pessoa agendou, em
   * qualquer instalação, mesmo uma fora do seu escopo de acesso atual.
   */
  listMeusAgendamentos(signal?: AbortSignal): Promise<Agendamento[]>;
  /** Sem `filtro`, o backend já limita a janela a ~30 dias a partir de agora
   * (nunca mais que ~31) — `filtro.de`/`filtro.ate` (YYYY-MM-DD) pedem outra
   * janela, mas o backend segue truncando pra no máximo esse teto. */
  listHistorico(signal?: AbortSignal, filtro?: { de?: string; ate?: string }): Promise<EventoHistorico[]>;
  listNovidades(signal?: AbortSignal): Promise<Novidade[]>;
  /** Restrito pela capability `gestao-novidades` no backend — a tela já
   * esconde o botão pra quem não tem, isto aqui é o cinto de segurança. */
  criarNovidade(input: NovidadeInput, signal?: AbortSignal): Promise<Novidade>;
  /** Idem — mesma capability, mesmo cinto de segurança. */
  excluirNovidade(id: string, signal?: AbortSignal): Promise<void>;
  listVersoes(instalacaoId: string, signal?: AbortSignal): Promise<VersoesInstalacao>;
  listDisponibilidade(data: string, signal?: AbortSignal): Promise<HorarioDisponivel[]>;
  /** Segura a(s) vaga(s) do horário por alguns minutos — chamado ao SELECIONAR, não ao confirmar. */
  reterHorario(
    input: { data: string; hora: string; instalacoesAlvo: string[] },
    signal?: AbortSignal,
  ): Promise<RetencaoHorario>;
  /**
   * Libera antes do prazo — ao trocar de horário ou sair da tela sem confirmar.
   * `melhorEsforco` pede pro navegador tentar entregar mesmo com a página
   * fechando (F5, fechar aba) — usado no `pagehide`, onde não há tempo de
   * esperar uma resposta.
   */
  liberarRetencao(holdId: string, opcoes?: { signal?: AbortSignal; melhorEsforco?: boolean }): Promise<void>;
  criarAgendamento(input: NovoAgendamento, signal?: AbortSignal): Promise<void>;
  cancelarAgendamento(id: string, signal?: AbortSignal): Promise<void>;
  /**
   * Bypassa a espera do RedBeat: dispara agora, sem esperar o horário
   * agendado. Restrito a sysadmin no backend (403 pra qualquer outro papel)
   * — a tela só esconde o botão pra quem não tem, é o backend quem garante.
   */
  dispararAgendamentoAgora(id: string, signal?: AbortSignal): Promise<void>;
  /**
   * "Personificar Instalação" — busca entre TODAS as instalações do sistema
   * (não só as do usuário), restrita pela capability `personificar-instalacao`
   * no backend. Usada só no seletor da tela de personificação.
   */
  buscarInstalacoesParaPersonificar(query: string, signal?: AbortSignal): Promise<InstalacaoBusca[]>;
  /**
   * Registra na auditoria da instalação que uma sessão de personificação foi
   * iniciada — chamado UMA VEZ, ao confirmar no diálogo (não a cada request
   * feita durante a sessão). Melhor esforço: nunca deve travar/derrubar o
   * fluxo de personificação em si.
   */
  registrarPersonificacao(instalacaoInfraId: number, signal?: AbortSignal): Promise<void>;
  /**
   * Abre (ou renova, chamado em heartbeat) a trava contra personificação
   * simultânea da mesma instalação — rejeita (erro) se outra pessoa já
   * estiver personificando agora. TTL curto no backend: parar de renovar
   * (aba fechada, PC travado) libera sozinho, sem exigir "Sair".
   */
  abrirOuRenovarSessaoPersonificacao(instalacaoInfraId: number, signal?: AbortSignal): Promise<void>;
  /**
   * Libera a trava antes do TTL — ao clicar "Sair" ou ao fechar a aba
   * (`melhorEsforco`, mesmo padrão de `liberarRetencao`). Não chamar não é
   * erro, só demora mais pra instalação liberar pros outros.
   */
  encerrarSessaoPersonificacao(
    instalacaoInfraId: number,
    opcoes?: { signal?: AbortSignal; melhorEsforco?: boolean },
  ): Promise<void>;
}

export interface FiltroInstalacoes {
  busca?: string;
  /** `true` só ANA, `false` só não-ANA, ausente = todas. */
  ana?: boolean;
  /** `true` só clientes internos, `false` só externos, ausente = todos. */
  interno?: boolean;
  limit: number;
  offset: number;
}

export interface PaginaInstalacoes {
  itens: Instalacao[];
  total: number;
  /** Há alguma instalação ANA / de cliente interno no escopo (ignora os filtros). */
  temAna: boolean;
  temInterno: boolean;
}

/** Resultado enxuto da busca de personificação — não é o `Instalacao` completo. */
export interface InstalacaoBusca {
  id: string;
  nome: string;
  codigo: string | null;
  /** Id de `pan.instalacoes_infra` — usado só pra registrar auditoria ao
   * confirmar a personificação, nada a ver com `id` (tenant, vira o header). */
  instalacaoInfraId: number | null;
  ana: boolean;
  interno: boolean;
}
